import { Box, Grid, FormControl, TextField, InputLabel, Select, MenuItem, Typography, useMediaQuery, useTheme, Button, Stack, SvgIcon, Checkbox, Link, FormControlLabel, FormGroup, Autocomplete } from '@mui/material';
import { client, StoreItem, StoreODS } from '../../../../app/services/api/orderManagementClient';
import PageArea from '../../../../components/shared/PageArea';
import PageTitle from '../../../../components/shared/PageTitle';
import { UserContext } from '../../../../components/shared/useUser';
import LoadingWheel from '../../../../components/ui/LoadingWheel';
import { ChangeEventHandler, SyntheticEvent, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Save01 } from '../../../../assets';
import { Download, Upload } from '@mui/icons-material';
import { Section } from '../../../../components/ui/Section';
import useLogError from '../../../../hooks/useLogError';
import { Store } from '../../../../app/services/api/generated';
import { MarketStoreFilterOptions } from '../../../../app/services/api/orderManagementClient';
import { OdsTypeEnum } from '../../../../pages/ManageAutoshipments/enums/OdsTypeEnum';
import DatePickerLocalized from '../../../../components/shared/DatePickerLocalized';
import StoreODSTable from './StoreODSTable';
import { StoreItemModel, StoreAvailableODS } from '../../../../app/services/api/orderManagementClient';
import StoreItemTable from './StoreItemTable';
import { AutoshipTemplateModel } from '../../../../app/services/api/orderManagementClient';
import { AutoshipSchedule } from '../../../../app/services/api/orderManagementClient';
import AutoshipUploadFileModal from '../../modals/AutoshipUploadFileModal';
import MessagesSnackbar from '../../../../components/shared/MessagesSnackbar';
import CustomBreadCrumbs from '../../../../components/ui/CustomBreadCrumbs';
import { Link as RouterLink } from 'react-router-dom';
import { AutoshipTemplateStatusEnum } from '../../enums/AutoshipmentTemplateStatusEnum';
import { convertToJsonNetDate, convertFromJsonNetDate, filterStoreOptions } from '../../../../utils';

const CreateOrEditAutoshipmentSchedule = () => {
  const { id } = useParams();
  const location = useLocation();
  const isNewFromTemplate = location.pathname.includes('/schedule/new/');
  const theme = useTheme();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { hasPermissionTo, isAdmin, selectedStore } = useContext(UserContext);
  const hasWritePermissions = hasPermissionTo(['AutoshipmentWrite']) || isAdmin();
  const { t } = useTranslation('autoshipment');
  const [isLoading, setIsLoading] = useState(true);
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.sm}px)`);
  const navigate = useNavigate();
  const { logError } = useLogError();
  const [stores, setStores] = useState<Store[]>([]);
  const [selectedStores, setSelectedStores] = useState<Store[]>([]);
  const [checkedAllStores, setCheckedAllStores] = useState<boolean>(false);
  const [deliverySchedules, setDeliverySchedules] = useState<StoreAvailableODS[]>([]);
  const [templateDetails, setTemplateDetails] = useState<AutoshipTemplateModel | null>(null);
  const [itemStores, setItemStores] = useState<StoreItemModel[]>([]);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string>('');
  const [uploadStoreModalOpen, setUploadStoreModalOpen] = useState(false);
  const [uploadStoreErrorMessage, setUploadStoreErrorMessage] = useState<string>('');

  const ODS_TYPE_OPTIONS = [
    { value: OdsTypeEnum.Store, label: 'Store' },
    { value: OdsTypeEnum.Custom, label: 'Custom' },
    { value: OdsTypeEnum.Emergency, label: 'Emergency' },
  ];

  const [formValues, setFormValues] = useState({
    id: id ? parseInt(id, 10) : null as number | null | undefined,
    templateId: -1 as number | null | undefined,
    company: '',
    customerType: '',
    orderType: '' as string | null,
    name: '',
    odsTypeId: null as number | null | undefined,
    publishDate: '' as string | null | undefined,
    shipDate: '' as string | null | undefined,
    deliveryDate: null as string | null | undefined,
    stores: [] as StoreODS[],
    items: [] as StoreItemModel[],
    statusId: null as number | null,
  });

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'error' | 'warning' | 'info' | 'success';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  useEffect(() => {
    if (formValues.odsTypeId == Number(OdsTypeEnum.Emergency)) {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const todayString = `${year}-${month}-${day}`;
      
      setFormValues(prev => ({
        ...prev,
        publishDate: todayString,
      }));
    }
  }, [formValues?.odsTypeId]);

  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);
      try {
        if (isNewFromTemplate) {
          const autoshipTemplateRes = await client.getAutoshipTemplateById(id ?? '', selectedStore?.storeNumber ?? '', true);
          
          if(autoshipTemplateRes.data.status != Number(AutoshipTemplateStatusEnum.ACTIVE)) {
            handleSnackbarOpen(t('templateNotActive'), 'error');
            return;
          }

          setTemplateDetails(autoshipTemplateRes.data);
          
          // Initialize form with template data
          setFormValues({
            ...formValues,
            templateId: parseInt(id!, 10),
            company: autoshipTemplateRes.data.company || '',
            customerType: autoshipTemplateRes.data.customerType || '',
            orderType: autoshipTemplateRes.data.orderType || '',
            odsTypeId: autoshipTemplateRes.data.odsTypeId || null,
          });

          if (autoshipTemplateRes.data.companyId && autoshipTemplateRes.data.customerTypeId) {
            await fetchMarketStores(autoshipTemplateRes.data.companyId, autoshipTemplateRes.data.customerTypeId);
          }

          if (autoshipTemplateRes.data.expandedItems && selectedStores.length > 0) {
            const itemsWithStores = expandItemsForStores(autoshipTemplateRes.data.expandedItems, selectedStores);
            setItemStores(itemsWithStores);
          }
        } else {
          // Editing existing schedule - fetch both schedule and template
          const autoshipScheduleRes = await client.getAutoshipScheduleById(Number(id), selectedStore?.storeNumber ?? '');
          setFormValues({
            ...autoshipScheduleRes.data,
            id: autoshipScheduleRes.data.id ?? null,
            odsTypeId: autoshipScheduleRes.data.odsTypeId ?? null,
            publishDate: convertFromJsonNetDate(autoshipScheduleRes.data?.publishDate),
            shipDate: convertFromJsonNetDate(autoshipScheduleRes.data?.shipDate),
            deliveryDate: convertFromJsonNetDate(autoshipScheduleRes.data?.deliveryDate),
            templateId: autoshipScheduleRes.data?.templateId,
          });
          
          const templateId = autoshipScheduleRes.data?.templateId;
          
          if (!autoshipScheduleRes.data?.templateId) {
            handleSnackbarOpen(t('errorLoadingData'), 'error');
            return;
          }
          
          const autoshipTemplateRes = await client.getAutoshipTemplateById(
            templateId?.toString() ?? '', 
            selectedStore?.storeNumber ?? '', 
            true,
          );
          
          setTemplateDetails(autoshipTemplateRes.data);

          if (autoshipTemplateRes.data.companyId && autoshipTemplateRes.data.customerTypeId) {
            await fetchMarketStores(autoshipTemplateRes.data.companyId, autoshipTemplateRes.data.customerTypeId, autoshipScheduleRes.data.stores);
          }

          if (autoshipTemplateRes.data.expandedItems && selectedStores.length > 0) {
            const itemsWithStores = expandItemsForStores(autoshipTemplateRes.data.expandedItems, selectedStores);
            setItemStores(itemsWithStores);
          }
        }
      } catch (error) {
        console.error('Error fetching initial data:', error);
        handleSnackbarOpen(t('errorLoadingData'), 'error');
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchInitialData();
    }
  }, []);

  const expandItemsForStores = (
    items: StoreItemModel[],
    stores: Store[],
  ): StoreItemModel[] => {
    const expandedItems: StoreItemModel[] = [];
    
    items.forEach(item => {
      stores.forEach(store => {
        expandedItems.push({
          ...item,
          id: null,
          customerId: store.customerId!,
          storeNumber: store.storeNumber,
          storeName: store.storeName,
          quantity: item.quantity == 0 ? null : item.quantity,
          lotNumber: item.lotNumber || '',
        });
      });
    });
    
    return expandedItems;
  };

  // Helper to merge with existing items
  const mergeWithExistingItems = (
    expandedItems: StoreItemModel[],
    existingItems: StoreItemModel[],
  ): StoreItemModel[] => {
    return expandedItems.map(item => {
      const existingItem = existingItems.find(
        existing => existing.customerId === item.customerId && existing.itemId === item.itemId,
      );
      
      if (existingItem) {
        return {
          ...item,
          id: existingItem.id,
          quantity: existingItem.quantity == 0 ? null : existingItem.quantity,
          lotNumber: existingItem.lotNumber,
        };
      }
      return item;
    });
  };

  useEffect(() => {
    if (templateDetails?.expandedItems && selectedStores.length > 0) {
      const expandedItems = expandItemsForStores(
        templateDetails.expandedItems,
        selectedStores,
      );
      
      // Merge with existing items from formValues
      const mergedItems = mergeWithExistingItems(expandedItems, formValues.items);
      setItemStores(mergedItems);
    } 
    else {
      setItemStores([]);
    }
  }, [selectedStores, templateDetails?.expandedItems, formValues?.items]);

  const fetchDeliverySchedules = async () => {
    if (!templateDetails?.companyId || formValues == null || formValues.odsTypeId != OdsTypeEnum.Store as number || templateDetails.orderTypeId == null || formValues.stores.length === 0) {
      setDeliverySchedules([]);
      return;
    }

    try {
      const storeNumbers = formValues.stores.map(s => s.storeNumber).join(',');
      const odsResult = await client.getDeliverySchedulesForStores(templateDetails.companyId, templateDetails.orderTypeId, storeNumbers);
      
      if (odsResult?.success && odsResult.data && odsResult.data.length > 0) {
        setDeliverySchedules(odsResult.data);
      } else {
        setDeliverySchedules([]);
      }
    } catch (ex) {
      console.error('Error fetching delivery schedules:', ex);
      setDeliverySchedules([]);
    }
  };

  useEffect(() => {
    if(formValues?.odsTypeId === OdsTypeEnum.Store as number) {
      fetchDeliverySchedules();
    }
  }, [formValues?.stores, formValues?.odsTypeId, checkedAllStores, templateDetails]);

  const fetchMarketStores = async (companyId?: number, customerTypeId?: number, existingStores?: StoreODS[]) => {
    try {
      const filterOptions: MarketStoreFilterOptions = {
        companyId: (companyId ?? templateDetails?.companyId)?.toString(),
        customerTypeId: (customerTypeId ?? templateDetails?.customerTypeId)?.toString(),
        searchTerm: undefined,
        pageNumber: 0,
        pageSize: 2147483647,
      };

      const response = await client.getMarketStores(filterOptions);
      
      if (response.success && response.data) {
        setStores(response.data.items || []);
        
        if (response.data.items) {
          const storesToMatch = existingStores ?? formValues.stores;
          setSelectedStores(
            response.data.items.filter(x => 
              storesToMatch.map(e => e.storeNumber).includes(x.storeNumber),
            ),
          );
        }
      } else {
        logError('Failed to load stores');
      }
    } catch (error) {
      logError('Failed to load stores');
    }
  };

  const handleSnackbarOpen = (message: string, severity: 'error' | 'warning' | 'info' | 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const isFormValid = (): unknown => {
    return selectedStores.length > 0 
    && formValues?.publishDate 
    && (formValues?.odsTypeId == OdsTypeEnum.Store || formValues?.deliveryDate)
    && formValues?.name?.trim() 
    && (formValues?.odsTypeId != OdsTypeEnum.Store || formValues?.stores.every(s => s.deliveryDate)) 
    && itemStores?.some(item => (item?.quantity != null && item?.quantity !== 0) || item.lotNumber?.trim())
    && !hasPublishDateError();
  };

  const handleSave = async (isPublish: boolean) => {
    if(!hasWritePermissions) return;
    
    setIsLoading(true);
    try {
      const model: AutoshipSchedule = {
        ...formValues,
        id: isNewFromTemplate ? null : id,
        publishDate: convertToJsonNetDate(formValues.publishDate ?? ''),
        shipDate: convertToJsonNetDate(formValues.deliveryDate ?? ''),
        deliveryDate: convertToJsonNetDate(formValues.deliveryDate ?? ''),
        templateId: id && isNewFromTemplate ? parseInt(id, 10) : templateDetails?.id,
        companyId: templateDetails?.companyId!,
        customerTypeId: templateDetails?.customerTypeId!,
        orderTypeId: templateDetails?.orderTypeId!,
        items: itemStores.filter(x => (x.quantity != null && x.quantity != 0) || x.lotNumber != ''),
        stores: formValues.stores,
        isPublish: isPublish,
        languageCode: localStorage.getItem('i18nextLng') ?? 'en-GB',
      };

      const result = await client.createOrUpdateAutoshipSchedule(model, selectedStore?.storeNumber ?? '');
      
      if (result.success && result.data > 0) {
        const successMessage = isPublish 
          ? t('schedulePublishedSuccessfully') 
          : isNewFromTemplate 
            ? t('scheduleCreatedSuccessfully') 
            : t('scheduleUpdatedSuccessfully');
        
        handleSnackbarOpen(successMessage, 'success');

        if (isPublish) {
          setFormValues(prev => ({
            ...prev,
            statusId: 1,
          }));
        }
        
        if (isNewFromTemplate) {
          navigate(`/manage-autoshipments/schedule/${result.data}`, { replace: true });
        }
      } else {
        handleSnackbarOpen(result.message, 'error');
      }
    } catch (error) {
      logError(error);
      handleSnackbarOpen(t('errorSavingSchedule') + ' ' + error, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  
  const handleSelectStoresChange = (event: SyntheticEvent, value: Store[]) => {
    setSelectedStores(value);

    const existingStoresMap = new Map(
      formValues.stores.map(store => [store.storeNumber, store]),
    );

    setFormValues(prev => ({
      ...prev,

      //keep stores in the grid if they aren't removed from select stores multi select for a better user experience
      stores: value.map(store => {
        const existingStore = existingStoresMap.get(store.storeNumber ?? '');
        if (existingStore) {
          return existingStore;
        }
        return {
          id: null,
          customerId: store?.customerId,
          storeNumber: store?.storeNumber,
          storeName: store?.storeName,
          deliveryDate: null,
        } as StoreODS;
      }),
    }));
  };

  const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setSelectedStores(checked ? stores : []);
    setFormValues(prev => ({
      ...prev,
      stores: checked ? stores.map(store => ({
        id: null,
        customerId: store.customerId,
        storeNumber: store.storeNumber,
        storeName: store.storeName,
        deliveryDate: null,
      } as StoreODS)) : [],
    }));
    setCheckedAllStores(checked);
  };

  const downloadItemsCsv = () => {
    setIsLoading(true);
    try {
      // Define CSV headers
      const headers = ['StoreNumber', 'StoreName', 'ItemNumber', 'ItemDescription', 'LOTNumber', 'Quantity'];
      
      // Convert itemStores to CSV rows
      const rows = itemStores.map(item => [
        item.storeNumber || '',
        item.storeName || '',
        item.itemNumber || '',
        item.itemDescription?.replace(',', '') || '',
        item.lotNumber || '',
        item.quantity || '',
      ]);
      
      // Combine headers and rows
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(',')),
      ].join('\n');
      
      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const downloadLink = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `${formValues?.name}-${timestamp}.csv`;
      
      downloadLink.href = url;
      downloadLink.download = filename;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(url);
      
      handleSnackbarOpen(t('itemsDownloaded'), 'success');
    } catch (error) {
      handleSnackbarOpen(t('downloadFailed') + ' ' + error, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormChange = (field: keyof typeof formValues) => 
    (event: React.ChangeEvent<HTMLInputElement | { value: unknown }>) => {
      setFormValues(prev => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const handleStoreItemFileSelected = async (file: File): Promise<{ success: boolean; data?: StoreItemModel[]; errorMessage?: string }> => {
    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
    
      if (lines.length < 2) {
        const errorMsg = t('csvInvalid');
        setUploadErrorMessage(errorMsg);
        return { success: false, data: [], errorMessage: errorMsg };
      }

      const requiredHeaders = ['StoreNumber', 'ItemNumber', 'LOTNumber', 'Quantity'];
      const allHeaders = ['StoreNumber', 'StoreName', 'ItemNumber', 'ItemDescription', 'LOTNumber', 'Quantity'];
    
      const headerLine = lines[0].split(',').map(col => col.trim().replace(/^"|"$/g, ''));
    
      const missingRequired = requiredHeaders.filter(header => 
        !headerLine.some(h => h.toLowerCase() === header.toLowerCase()),
      );
    
      if (missingRequired.length > 0) {
        const errorMsg = t('missingRequiredHeaders') + ': ' + missingRequired.join(', ');
        setUploadErrorMessage(errorMsg);
        return { success: false, data: [], errorMessage: errorMsg };
      }

      const headerMap = new Map<string, number>();
      headerLine.forEach((header, index) => {
        const matchingHeader = allHeaders.find(h => h.toLowerCase() === header.toLowerCase());
        if (matchingHeader) {
          headerMap.set(matchingHeader, index);
        }
      });

      const dataLines = lines.slice(1);
      const parsedItems: StoreItemModel[] = [];
      const invalidItems: StoreItemModel[] = [];

      for (let i = 0; i < dataLines.length; i++) {
        const line = dataLines[i];
        const columns = line.split(',').map(col => col.trim().replace(/^"|"$/g, ''));
      
        // Extract values using header mapping, with fallbacks for optional columns
        const storeNumber = columns[headerMap.get('StoreNumber') ?? -1] || '';
        const storeName = headerMap.has('StoreName') ? (columns[headerMap.get('StoreName')!] || '') : '';
        const itemNumber = columns[headerMap.get('ItemNumber') ?? -1] || '';
        const itemDescription = headerMap.has('ItemDescription') ? (columns[headerMap.get('ItemDescription')!] || '') : '';
        const lotNumber = columns[headerMap.get('LOTNumber') ?? -1] || '';
        const quantity = columns[headerMap.get('Quantity') ?? -1] || '';

        const matchingStore = selectedStores.find(s => s.storeNumber === storeNumber);

        const matchingItem = templateDetails?.expandedItems?.find(item => {
          if (item.itemNumber !== itemNumber) return false;
        
          // if itemDescription is provided in CSV, it must match
          if (itemDescription != '' && item.itemDescription?.replace(',', '') !== itemDescription.replace(',', '')) {
            return false;
          }
        
          return true;
        });

        let parsedQuantity = parseInt(quantity) || 0;
        if (parsedQuantity < 0) parsedQuantity = 0;

        if (!matchingStore || !matchingItem) {
          invalidItems.push({
            storeNumber: storeNumber,
            storeName: storeName || matchingStore?.storeName || '',
            itemNumber: itemNumber,
            itemDescription: itemDescription || matchingItem?.itemDescription?.replace(',', '') || '',
            lotNumber: lotNumber,
            quantity: parsedQuantity,
          } as StoreItemModel);
          continue;
        }

        parsedItems.push({
          id: null,
          customerId: matchingStore.customerId!,
          storeNumber: matchingStore.storeNumber,
          storeName: matchingStore.storeName,
          itemId: matchingItem.itemId,
          itemNumber: matchingItem.itemNumber,
          itemDescription: matchingItem.itemDescription,
          lotNumber: lotNumber || '',
          quantity: parsedQuantity,
        } as StoreItemModel);
      }

      if (invalidItems.length > 0) {
        const errorMsg = t('invalidItems');
        setUploadErrorMessage(errorMsg);
        return { success: false, data: invalidItems, errorMessage: errorMsg };
      }

      setUploadErrorMessage('');
      return { success: true, data: parsedItems };
    } catch (error) {
      const errorMsg = t('errorLoadingData') + ' ' + error;
      setUploadErrorMessage(errorMsg);
      return { success: false, data: [], errorMessage: errorMsg };
    }
  };

  const handleStoreItemConfirm = (uploadedItems: StoreItemModel[]) => {
    setItemStores(prev => {
      const updatedItems = prev.map(existingItem => {
        const uploadedItem = uploadedItems.find(
          uploaded => 
            uploaded.customerId === existingItem.customerId && 
            uploaded.itemId === existingItem.itemId,
        );

        if (uploadedItem) {
          return {
            ...existingItem,
            quantity: uploadedItem.quantity == 0 ? null : uploadedItem.quantity,
            lotNumber: uploadedItem.lotNumber,
          };
        } else {
          return {
            ...existingItem,
            quantity: null,
            lotNumber: '',
          };
        }
      });

      return updatedItems;
    });
    
    handleSnackbarOpen(
      t('successfullyUploaded') + `${uploadedItems.length}` + t('items'), 
      'success',
    );
  };

  const handleStoreFileSelected = async (file: File): Promise<{ success: boolean; data?: StoreODS[]; errorMessage?: string }> => {
    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        const errorMsg = t('csvInvalid');
        setUploadStoreErrorMessage(errorMsg);
        return { success: false, data: [], errorMessage: errorMsg };
      }

      const expectedHeaders = ['StoreNumber', 'StoreName', 'DeliveryDate'];
      const headerLine = lines[0].split(',').map(col => col.trim().replace(/^"|"$/g, ''));
      
      const headersMatch = expectedHeaders.every((header, index) => 
        headerLine[index]?.toLowerCase() === header.toLowerCase(),
      );
      
      if (!headersMatch || headerLine.length !== expectedHeaders.length) {
        const errorMsg = t('invalidCsvHeaders') + expectedHeaders.join(', ');
        setUploadStoreErrorMessage(errorMsg);
        return { success: false, data: [], errorMessage: errorMsg };
      }

      const dataLines = lines.slice(1);
      const parsedStores: StoreODS[] = [];
      const invalidStores: StoreODS[] = [];

      for (let i = 0; i < dataLines.length; i++) {
        const line = dataLines[i];
        const columns = line.split(',').map(col => col.trim().replace(/^"|"$/g, ''));
        
        if (columns.length !== 3) {
          const errorMsg = t('invalidCsvFormat') + `: Row ${i + 2} has ${columns.length} columns, expected 3 columns`;
          setUploadStoreErrorMessage(errorMsg);
          return { success: false, data: [], errorMessage: errorMsg };
        }

        const [storeNumber, storeName, deliveryDate] = columns;

        const matchingStore = selectedStores.find(s => 
          s.storeNumber === storeNumber,
        );
        const formStore = formValues.stores.find(s => s.storeNumber === storeNumber);

        const availableSchedule = deliverySchedules.find(schedule => 
          schedule.storeNumber == storeNumber &&
          schedule.deliveryDates?.some((date: string) => {
            if (date === deliveryDate) return true;

            const parts = deliveryDate.split('/');
            if (parts.length === 3) {
              const [day, month, year] = parts;
              return date === `${day.padStart(2, '0')}-${month.padStart(2, '0')}-${year}`;
            }
            return false;
          }),
        );

        if (!matchingStore || !availableSchedule) {
          invalidStores.push({
            id: 0,
            customerId: formStore!.customerId!,
            storeNumber: storeNumber,
            deliveryDate: deliveryDate || null,
          } as StoreODS);
          continue;
        }

        let parsedDeliveryDate: string | null = null;
        if (deliveryDate) {
          // Accept dd/MM/yyyy and convert to yyyy-MM-dd
          const dateParts = deliveryDate.split('/');
          if (dateParts.length === 3) {
            const [day, month, year] = dateParts;
            parsedDeliveryDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
          } else {
            // fallback: try yyyy-MM-dd
            const dashParts = deliveryDate.split('-');
            if (dashParts.length === 3) {
              const [year, month, day] = dashParts;
              parsedDeliveryDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
            }
          }
        }
        parsedStores.push({
          id: null,
          customerId: matchingStore.customerId!,
          storeNumber: matchingStore.storeNumber,
          deliveryDate: parsedDeliveryDate,
        } as StoreODS);
      }

      if (invalidStores.length > 0) {
        const errorMsg = t('invalidStoreItems');
        setUploadStoreErrorMessage(errorMsg);
        return { success: false, data: invalidStores, errorMessage: errorMsg };
      }

      setUploadStoreErrorMessage('');
      return { success: true, data: parsedStores.filter(store => store.deliveryDate != null && store.deliveryDate != '') };
    } catch (error) {
      const errorMsg = t('errorLoadingData') + error;
      setUploadStoreErrorMessage(errorMsg);
      return { success: false, data: [], errorMessage: errorMsg };
    }
  };

  const handleStoreConfirm = (uploadedStores: StoreODS[]) => {
    const validUploadedStores = uploadedStores.filter(store => {
      const hasValidDeliveryDate = store.deliveryDate != null && store.deliveryDate != '';
      return hasValidDeliveryDate;
    });

    setFormValues(prev => {
      const updatedStores = prev.stores.map(existingStore => {
        const uploadedStore = validUploadedStores.find(
          uploaded => uploaded.storeNumber === existingStore.storeNumber,
        );

        if (uploadedStore) {
          return {
            ...existingStore,
            deliveryDate: uploadedStore.deliveryDate,
          };
        } else {
          return {
            ...existingStore,
            deliveryDate: null,
          };
        }
      });

      return {
        ...prev,
        stores: updatedStores,
      };
    });

    setUploadStoreModalOpen(false);
    setUploadStoreErrorMessage('');
    
    handleSnackbarOpen(
      t('successfullyUploaded') + ` ${validUploadedStores.length}` + t('stores'), 
      'success',
    );
  };

  const downloadStoresCsv = () => {
    setIsLoading(true);
    try {
      const headers = ['StoreNumber', 'StoreName', 'DeliveryDate'];
      
      const rows = formValues.stores.map(store => {
        const matchingStore = selectedStores.find(s => s.customerId === store.customerId);

        let formattedDeliveryDate = '';
        if (store.deliveryDate) {
          const dateParts = (store.deliveryDate as unknown as String)?.split('-');
          if (dateParts.length === 3) {
            const [year, month, day] = dateParts;
            formattedDeliveryDate = `${day}/${month}/${year}`;
          }
        }

        return [
          store.storeNumber || '',
          matchingStore?.storeName || '',
          formattedDeliveryDate,
        ];
      });
      
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(',')),
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const downloadLink = document.createElement('a');
      const url = URL.createObjectURL(blob);

      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `${formValues?.name}-${timestamp}.csv`;
      
      downloadLink.href = url;
      downloadLink.download = filename;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(url);
      
      handleSnackbarOpen(t('storesDownloaded'), 'success');
    } catch (error) {
      handleSnackbarOpen(t('downloadFailed') + ' ' + error, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetQuantityForAll = (itemId: number, quantity: number) => {
    const uniqueCustomerIds = new Set(
      itemStores
        .filter(item => item.itemId === itemId)
        .map(item => item.customerId),
    );

    if (uniqueCustomerIds.size < 2) {
      handleSnackbarOpen(t('needMultipleStoresForCopy'), 'warning');
      return;
    }

    setItemStores(prev => 
      prev.map(item => 
        item.itemId === itemId 
          ? { ...item, quantity }
          : item,
      ),
    );
    
    handleSnackbarOpen(t('quantitySetForAllStores'), 'success');
  };

  const isPublishedOrCompleted = () => {
    return formValues.statusId != null ? formValues.statusId >= 1 : false;
  };

  const hasPublishDateError = (): boolean => {
    if (formValues.odsTypeId === OdsTypeEnum.Emergency) return false;

    // If ODS type is Store, check publishDate is after ALL store deliveryDates
    if (formValues.odsTypeId === OdsTypeEnum.Store) {
      if (!formValues.publishDate || !formValues.stores || formValues.stores.length === 0) return false;
      const publishDate = new Date(formValues.publishDate);
      // stores: StoreODS[]; each has deliveryDate
      return formValues.stores.some(store => {
        if (!store.deliveryDate) return false;
        return publishDate >= new Date(store.deliveryDate);
      });
    }

    // For other types, check publishDate is after deliveryDate
    return !!(
      formValues?.publishDate && 
      formValues?.deliveryDate &&
      new Date(formValues.publishDate) >= new Date(formValues.deliveryDate)
    );
  };

  const formatDeliveryDate = (dateStr?: string | null) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    if (!year || !month || !day) return dateStr;
    return `${day}/${month}/${year}`;
  };


  return (
    <PageArea>
      <Box>
        <CustomBreadCrumbs title={t('schedulenav')}>
          <Link to={`/manage-autoshipments/schedules/${templateDetails?.id}`}
            component={RouterLink}>{ t('schedulesnav') }</Link>
        </CustomBreadCrumbs>
      </Box>
      {templateDetails == null || isLoading ? <LoadingWheel /> : 
        <Box display='flex'
          width='100%'
          height='100%'
          flexDirection='column' >
          <Grid container
            sx={{ pt: '6px', pb: '12px' }}
            spacing={4}>
            <Stack width='100%'
              spacing={5}
              direction={isMobile ? 'column' : 'row'}
              sx={{ justifyContent: 'space-between' }}>
              <PageTitle subTitle=''>
                { isNewFromTemplate ? t('addSchedule') : t('scheduleDetails')}
              </PageTitle>
              {hasWritePermissions && !isPublishedOrCompleted() && (  
                <Box>
                  <Stack width='100%'
                    spacing={5}
                    direction={isMobile ? 'column' : 'row'}
                    sx={{ pb: 2 }}>
                    <Button 
                      variant="secondary" 
                      size='lg'
                      onClick={() => navigate(`/manage-autoshipments/schedules/${templateDetails?.id}`)}>
                      {t('cancelBtn')}
                    </Button>
                    <Button
                      variant='primary'
                      size='lg'
                      startIcon={<SvgIcon><Save01 /></SvgIcon>}
                      onClick={() => handleSave(false)}>
                      {t('saveBtn')}
                    </Button>
                    <Button
                      variant='primary'
                      size='lg'
                      startIcon={<SvgIcon><Save01 /></SvgIcon>}
                      onClick={() => handleSave(true)}
                      disabled={!isFormValid() || isNewFromTemplate}
                    >
                      {t('publishBtn')}
                    </Button>
                  </Stack>
                </Box>
              )}
            </Stack>
            <Grid container
              mt={5}>
              <Grid container
                spacing={5}
                flexWrap={isMobile ? 'wrap' : 'nowrap'}
              >
                <Grid item
                  xs={12}
                  sm={6}
                  md={4}
                  sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left' }}>
                  <FormControl fullWidth>
                    <TextField
                      label={t('instance')}
                      size='small'
                      required
                      variant='outlined'
                      value={templateDetails?.company ?? ''}
                      disabled={true}
                    >
                    </TextField>
                  </FormControl>
                </Grid>
                <Grid item
                  xs={12}
                  sm={6}
                  md={4}
                  sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left' }}>
                  <FormControl fullWidth>
                    <TextField
                      label={t('market')}
                      size='small'
                      required
                      variant='outlined'
                      value={templateDetails?.customerType ?? ''}
                      disabled={true}
                    >
                    </TextField>
                  </FormControl>
                </Grid>
                <Grid item
                  xs={12}
                  xl={4}
                  md={4}
                  sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left' }}>
                  <FormControl fullWidth
                    size='small'>
                    <TextField
                      label={t('orderType')}
                      size='small'
                      required
                      variant='outlined'
                      value={templateDetails?.orderType ?? ''}
                      disabled={true}
                    >
                    </TextField>
                  </FormControl>
                </Grid>
                <Grid item
                  sm={6}
                  xs={12}
                  md={2.6}>
                  <FormControl fullWidth>
                    <Autocomplete
                      fullWidth
                      multiple
                      limitTags={2}
                      options={stores ?? []}
                      value={selectedStores}
                      disabled={checkedAllStores || !hasWritePermissions || isPublishedOrCompleted()}
                      onChange={handleSelectStoresChange}
                      getOptionKey={(option) => option.storeNumber ?? ''}
                      getOptionLabel={(option) => option.storeName ?? ''}
                      filterOptions={filterStoreOptions}
                      renderInput={(params) => (
                        <TextField {...params}
                          required={selectedStores.length === 0 && !checkedAllStores}
                          label={t('selectStores')}
                          placeholder={t('selectStores')} />
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid item
                  sm={6}
                  xs={12}
                  md={1.4}>
                  <FormGroup style={{ textAlign: 'right' }}>
                    <FormControlLabel
                      control={<Checkbox
                        checked={checkedAllStores}
                        disabled={!hasWritePermissions || isPublishedOrCompleted()}
                        onChange={handleSelectAllChange} />}
                      label={t('selectAll')}
                    />
                  </FormGroup>
                </Grid>
              </Grid>
              <Grid container 
                spacing={5}
                flexWrap={isMobile ? 'wrap' : 'nowrap'}
                mt={5}
                flexDirection={'column'}>
                <Grid item
                  xs={12}
                  sm={6}
                  md={3}
                  sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left' }}>
                  <FormControl fullWidth
                    size='small'>
                    <TextField
                      label={t('name')}
                      size='small'
                      required
                      variant='outlined'
                      disabled={!hasWritePermissions || isPublishedOrCompleted()}
                      value={formValues?.name ?? ''}
                      onChange={handleFormChange('name')}
                    >
                    </TextField>
                  </FormControl>
                </Grid>

                <Grid item
                  xs={12}
                  sm={6}
                  md={3}
                  mt={5}
                  sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left' }}>
                  <FormControl required
                    fullWidth>
                    <InputLabel shrink>
                      {t('odsType')}
                    </InputLabel>
                    <Select
                      required
                      value={formValues?.odsTypeId!}
                      disabled={!hasWritePermissions || isPublishedOrCompleted()}
                      onChange={(e) => {
                        setFormValues(prev => ({
                          ...prev,
                          odsTypeId: Number(e.target.value),
                        }));
                      }}
                      label={t('odsType')}
                      displayEmpty
                      notched
                    >
                      {ODS_TYPE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} 
                          value={option.value.toString()}>
                          {t(option.label)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>     
              <Grid container
                spacing={5}
                flexWrap={isMobile ? 'wrap' : 'nowrap'}
                mt={5}
                flexDirection={'row'}
              >
                <Grid item
                  xs={12}
                  sm={6}
                  md={3}>
                  <DatePickerLocalized
                    disabled={!hasWritePermissions || formValues?.odsTypeId == OdsTypeEnum.Emergency || isPublishedOrCompleted()}
                    useCalendar={false}
                    format="DD/MM/YYYY"
                    label={t('publishDate')}
                    translations={{
                      clearButtonLabel: t('clearButtonLabel'),
                      todayButtonLabel: t('todayButtonLabel'),
                      cancelButtonLabel: t('cancelButtonLabel'),
                      okButtonLabel: t('okButtonLabel'),
                    }}
                    value={formValues?.publishDate}
                    onChange={(newValue) => {
                      if (newValue) {
                        setFormValues(prev => ({
                          ...prev,
                          publishDate: newValue,
                        }));
                      }
                    }}
                    shouldDisableDate={(date) => {
                      const d = new Date(date);
                      d.setHours(0, 0, 0, 0);
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const tomorrow = new Date(today);
                      tomorrow.setDate(today.getDate() + 1);

                      return formValues.odsTypeId == OdsTypeEnum.Emergency
                        ? d < today
                        : d < tomorrow;
                    }}
                  />
                  {hasPublishDateError() && (
                    <Typography
                      variant='textXS'
                      color='error'
                      sx={{ mt: 0.5, display: 'block' }}
                    >
                      {t('publishDateMustBeBeforeDeliveryDate')}
                    </Typography>
                  )}
                </Grid>
                {formValues.odsTypeId != OdsTypeEnum.Store && (
                  <Grid item
                    xs={12}
                    sm={6}
                    md={3}>
                    <DatePickerLocalized
                      disabled={!hasWritePermissions || isPublishedOrCompleted()}
                      useCalendar={false}
                      format="DD/MM/YYYY"
                      label={t('deliveryDate')}
                      translations={{
                        clearButtonLabel: t('clearButtonLabel'),
                        todayButtonLabel: t('todayButtonLabel'),
                        cancelButtonLabel: t('cancelButtonLabel'),
                        okButtonLabel: t('okButtonLabel'),
                      }}
                      value={formValues?.deliveryDate}
                      onChange={(newValue) => {
                        if (newValue) {
                          setFormValues(prev => ({
                            ...prev,
                            deliveryDate: newValue,
                          }));
                        }
                      }}
                      shouldDisableDate={(date) => {
                        const d = new Date(date);
                        d.setHours(0, 0, 0, 0);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const tomorrow = new Date(today);
                        tomorrow.setDate(today.getDate() + 1);

                        return formValues.odsTypeId == OdsTypeEnum.Emergency
                          ? d < today
                          : d < tomorrow;
                      }}
                    />
                  </Grid>)}
              </Grid>
            </Grid>
          </Grid>
        </Box >}
      {isLoading ? null : 
        <Box mt={5}> 
          <Grid item
            lg={6}
            xs={12}>
            {formValues.odsTypeId == OdsTypeEnum.Store as number && formValues?.stores.length > 0 ? 
              <Box>
                <Stack width='100%'
                  spacing={2}
                  direction={isMobile ? 'column' : 'row'}
                  sx={{
                    justifyContent: 'space-between',
                    pb: 2,
                  }}>
                  <Typography
                    variant='displaySM'
                    sx={{ fontWeight: 600 }}>{t('storesOds')}</Typography>
                  {hasWritePermissions && !isPublishedOrCompleted() && <Stack spacing={5}
                    direction={'row'}>
                    <Button
                      variant='secondary'
                      size='lg'
                      startIcon={<SvgIcon><Download /></SvgIcon>}
                      onClick={downloadStoresCsv}
                    >
                      {t('download')}
                    </Button>
                    <Button
                      variant='secondary'
                      size='lg'
                      onClick={() => setUploadStoreModalOpen(true)}
                      startIcon={<SvgIcon><Upload /></SvgIcon>}
                    >
                      {t('upload')}
                    </Button>
                  </Stack>
                  }
                </Stack>
                <Section> 
                  <StoreODSTable
                    list={formValues?.stores}
                    isLoading={isLoading}
                    hasWritePermissions={hasWritePermissions && !isPublishedOrCompleted()}
                    deliverySchedules={deliverySchedules}
                    handleDeliveryDateChange={(item: StoreODS) => {
                      setFormValues(prev => ({
                        ...prev,
                        stores: prev.stores.map(store => store.customerId === item.customerId ? item : store),
                      }));
                    }}
                    publishDate={formValues?.publishDate}
                  />
                </Section> 
              </Box>
              : null
            }
          </Grid>
        </Box>
      }
      {!isLoading && itemStores.length > 0 ?
        <Box mt={5}> 
          <Grid item
            sx={{ pt: '1em' }}
            lg={6}
            xs={12}>
            <Stack width='100%'
              spacing={2}
              direction={isMobile ? 'column' : 'row'}
              sx={{
                justifyContent: 'space-between',
              }}>
              <Typography
                variant='displaySM'
                sx={{ fontWeight: 600 }}>{t('itemSelection')}</Typography>
              {hasWritePermissions && !isPublishedOrCompleted() && <Stack spacing={5}
                direction={'row'}
                sx={{ pb: '12px' }}>
                <Button
                  variant='secondary'
                  size='lg'
                  startIcon={<SvgIcon><Download /></SvgIcon>}
                  onClick={downloadItemsCsv}
                >
                  {t('download')}
                </Button>
                <Button
                  variant='secondary'
                  size='lg'
                  onClick={() => setUploadModalOpen(true)}
                  startIcon={<SvgIcon><Upload /></SvgIcon>}
                >
                  {t('upload')}
                </Button>
              </Stack>
              }
            </Stack>
            <StoreItemTable
              hasWritePermissions={hasWritePermissions && !isPublishedOrCompleted()}
              list={
                hasWritePermissions && !isPublishedOrCompleted() ? itemStores : itemStores.filter(x => (x.quantity != null && x.quantity != 0) || x.lotNumber != '')
              }
              isLoading={isLoading}
              handleItemChange={(item: StoreItemModel) => {
                setItemStores(prev => 
                  prev.map(i => 
                    i.itemId === item.itemId && i.customerId === item.customerId ? item : i,
                  ),
                );
              }}
              handleSetQuantityForAll={handleSetQuantityForAll}
            />
          </Grid>
        </Box>
        : <Box></Box>
      }

      <AutoshipUploadFileModal<StoreItemModel>
        open={uploadModalOpen}
        onClose={() => {
          setUploadModalOpen(false);
          setUploadErrorMessage('');
        }}
        onFileSelected={handleStoreItemFileSelected}
        onConfirm={handleStoreItemConfirm}
        errorMessage={uploadErrorMessage}
        columns={[
          { header: t('storeNumber'), accessor: (item: StoreItemModel) => item.storeNumber ?? '' },
          { header: t('storeName'), accessor: (item: StoreItemModel) => item.storeName ?? '' },
          { header: t('itemNumber'), accessor: (item: StoreItemModel) => item.itemNumber ?? '' },
          { header: t('quantity'), accessor: (item: StoreItemModel) => item.quantity == 0 ? '' : (item.quantity ?? '') },
          { header: t('lotNumber'), accessor: (item: StoreItemModel) => item.lotNumber ?? '' },
        ]}
        invalidItemRenderer={(item: StoreItemModel) => `${t('storeNumber')}: ${item.storeNumber ?? ''}, ${t('itemNumber')}: ${item.itemNumber ?? ''}, ${t('itemDescription')}: ${item.itemDescription ?? ''}`}
      />

      <AutoshipUploadFileModal<StoreODS>
        open={uploadStoreModalOpen}
        onClose={() => {
          setUploadStoreErrorMessage('');
          setUploadStoreModalOpen(false);
        }}
        onFileSelected={handleStoreFileSelected}
        onConfirm={handleStoreConfirm}
        errorMessage={uploadStoreErrorMessage}
        columns={[
          { header: t('storeNumber'), accessor: (store: StoreODS) => store.storeNumber },
          { header: t('deliveryDate'), accessor: (store: StoreODS) => formatDeliveryDate(store.deliveryDate?.toString() ?? '' ) },
        ]}
        invalidItemRenderer={(store: StoreODS) => `${t('storeNumber')}: ${store.storeNumber ?? ''}, ${t('deliveryDate')}: ${store.deliveryDate ?? ''}`}
      />

      <MessagesSnackbar 
        open={snackbar.open}
        onClose={() => handleSnackbarClose()}
        message={snackbar.message}
        severity={snackbar.severity}
        duration={5000} 
      />
    </PageArea >
  );
};

export default CreateOrEditAutoshipmentSchedule;